import { useEffect, useRef, useState } from "react";
import {
  applyPrice,
  contractArticles,
  contractIntro,
  contractTotal,
  formatMoney,
  prefillContract,
  type ContractDraft,
  type ContractSource,
  type ContractStatus,
} from "../../shared/contract";
import { api } from "../api";
import { messageOf } from "./ui";

type Loaded = { body: ContractDraft | null; status: ContractStatus | null };

export function ContractForm({ visitId, source, canEdit, onSent }: { visitId: string | null; source: ContractSource; canEdit: boolean; onSent?: () => void }) {
  const [draft, setDraft] = useState<ContractDraft>(() => prefillContract(source));
  const [status, setStatus] = useState<ContractStatus | null>(null);
  const [ready, setReady] = useState(!visitId);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sourceRef = useRef(source);
  sourceRef.current = source;

  useEffect(() => {
    if (!visitId) return;
    let cancel = false;
    api<Loaded>(`/api/tastings/${visitId}/contract`)
      .then((result) => {
        if (cancel) return;
        setDraft(result.body ?? prefillContract(sourceRef.current));
        setStatus(result.status);
        setReady(true);
      })
      .catch((reason: unknown) => {
        if (cancel) return;
        setError(messageOf(reason));
        setReady(true);
      });
    return () => {
      cancel = true;
    };
  }, [visitId]);

  function patch(partial: Partial<ContractDraft>) {
    setDraft((current) => applyPrice(current, { ...current, ...partial }));
    setNote(null);
  }

  async function save() {
    if (!visitId || !canEdit) return;
    setSaving(true);
    setError(null);
    try {
      const result = await api<Loaded>(`/api/tastings/${visitId}/contract`, { method: "PUT", body: JSON.stringify(draft) });
      setDraft(result.body ?? draft);
      setStatus(result.status);
      setNote("Αποθηκεύτηκε.");
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setSaving(false);
    }
  }

  async function send() {
    if (!visitId || !canEdit || sending) return;
    setSending(true);
    setError(null);
    try {
      const result = await api<{ status: ContractStatus; sent: boolean }>(`/api/tastings/${visitId}/contract/send`, { method: "POST", body: JSON.stringify(draft) });
      setStatus(result.status);
      setNote(result.sent ? "Το mail με τον σύνδεσμο υπογραφής έφυγε." : "Έχει ήδη υπογραφεί.");
      if (result.sent) onSent?.();
    } catch (reason: unknown) {
      setError(messageOf(reason));
    } finally {
      setSending(false);
    }
  }

  const total = contractTotal(draft);
  const articles = contractArticles(draft);
  const locked = !canEdit;

  return (
    <article className="panel contract-sheet">
      <h2>Ιδιωτικό συμφωνητικό</h2>
      <p className="meta">Όσα ξέρουμε από τα προηγούμενα βήματα είναι συμπληρωμένα. Τα κενά τα γράφεις εδώ. Η αποστολή στέλνει σύνδεσμο στο mail του πελάτη για υπογραφή.</p>
      {error ? <p className="banner error">{error}</p> : null}
      {note ? <p className="banner">{note}</p> : null}
      {status?.state === "signed" ? <p>Υπογράφηκε από {status.signer_name}.</p> : status?.state === "sent" ? <p>Στάλθηκε. Ο πελάτης δεν έχει υπογράψει ακόμα.</p> : null}
      {!ready ? <p>Φόρτωση συμφωνητικού…</p> : (
        <>
          <div className="fields">
            <Field label="Τόπος" value={draft.signed_place} disabled={locked} onChange={(signed_place) => patch({ signed_place })} />
            <Field label="Ημερομηνία συμφωνητικού" type="date" value={draft.signed_on} disabled={locked} onChange={(signed_on) => patch({ signed_on })} />
            <Field label="Εταιρεία" value={draft.company_name} disabled={locked} onChange={(company_name) => patch({ company_name })} />
            <Field label="Έδρα" value={draft.company_seat} disabled={locked} onChange={(company_seat) => patch({ company_seat })} />
            <Field label="Διεύθυνση εταιρείας" value={draft.company_address} disabled={locked} onChange={(company_address) => patch({ company_address })} />
            <Field label="ΑΦΜ εταιρείας" value={draft.company_afm} disabled={locked} onChange={(company_afm) => patch({ company_afm })} />
            <Field label="ΔΟΥ" value={draft.company_doy} disabled={locked} onChange={(company_doy) => patch({ company_doy })} />
            <Field label="Χώρος" value={draft.venue_name} disabled={locked} onChange={(venue_name) => patch({ venue_name })} />
            <Field label="Εκπρόσωπος" value={draft.representative} disabled={locked} onChange={(representative) => patch({ representative })} />
          </div>

          {draft.parties.map((party, index) => (
            <fieldset className="party-card" key={index}>
              <legend>Πελάτης {index + 1}</legend>
              <div className="fields">
                <Field label="Ονοματεπώνυμο" value={party.name} disabled={locked} onChange={(name) => setParty(index, { name })} />
                <Field label="Α.Δ.Τ." value={party.id_number} disabled={locked} onChange={(id_number) => setParty(index, { id_number })} />
                <Field label="Α.Φ.Μ." value={party.tax_id} disabled={locked} onChange={(tax_id) => setParty(index, { tax_id })} />
                <Field label="Τηλέφωνο" value={party.phone} disabled={locked} onChange={(phone) => setParty(index, { phone })} />
                <Field label="Email" value={party.email} disabled={locked} onChange={(email) => setParty(index, { email })} />
                <Field label="Οδός και αριθμός" value={party.street} disabled={locked} onChange={(street) => setParty(index, { street })} />
                <Field label="Πόλη" value={party.city} disabled={locked} onChange={(city) => setParty(index, { city })} />
                <Field label="Τ.Κ." value={party.postal_code} disabled={locked} onChange={(postal_code) => setParty(index, { postal_code })} />
              </div>
            </fieldset>
          ))}
          {canEdit && draft.parties.length < 4 ? <button type="button" className="secondary" onClick={() => patch({ parties: [...draft.parties, { name: "", id_number: "", tax_id: "", phone: "", email: "", street: "", city: "", postal_code: "" }] })}>+ Πελάτης</button> : null}

          <h3>Εκδήλωση</h3>
          <div className="fields">
            <Field label="Είδος" value={draft.event_kind} disabled={locked} onChange={(event_kind) => patch({ event_kind })} />
            <Field label="Ημερομηνία" value={draft.event_date_label} disabled={locked} onChange={(event_date_label) => patch({ event_date_label })} />
            <Field label="Έναρξη" value={draft.start_time} disabled={locked} onChange={(start_time) => patch({ start_time })} />
            <Field label="Λήξη" value={draft.end_time} disabled={locked} onChange={(end_time) => patch({ end_time })} />
            <Field label="Μυστήριο" value={draft.ceremony} disabled={locked} onChange={(ceremony) => patch({ ceremony })} />
            <Field label="Άτομα" value={draft.guests} disabled={locked} onChange={(guests) => patch({ guests })} />
            <Field label="Ελάχιστη χρέωση (άτομα)" value={draft.minimum_guests} disabled={locked} onChange={(minimum_guests) => patch({ minimum_guests })} />
            <Field label="Σερβίρισμα" value={draft.service} disabled={locked} onChange={(service) => patch({ service })} />
            <Field label="Περιγραφή χώρου" value={draft.venue_place} disabled={locked} onChange={(venue_place) => patch({ venue_place })} />
            <Field label="Μενού" value={draft.menu} disabled={locked} onChange={(menu) => patch({ menu })} />
          </div>

          <h3>Τίμημα</h3>
          <div className="fields">
            <Field label="Τιμή ανά άτομο" value={draft.price_per_person} disabled={locked} onChange={(price_per_person) => patch({ price_per_person })} />
            <p className="meta">Σύνολο: {total != null ? formatMoney(total) : "συμπλήρωσε τιμή και άτομα"}</p>
          </div>
          <label>Φ.Π.Α.<textarea value={draft.vat_note} disabled={locked} onChange={(event) => patch({ vat_note: event.target.value })} /></label>
          <label>Παιδιά<textarea value={draft.child_rule} disabled={locked} onChange={(event) => patch({ child_rule: event.target.value })} /></label>

          <h3>Πρόσθετα</h3>
          <div className="fields">
            <Field label="DJ εταιρείας" value={draft.dj_company} disabled={locked} onChange={(dj_company) => patch({ dj_company })} />
            <Field label="Ήχος για DJ πελάτη" value={draft.dj_client_sound} disabled={locked} onChange={(dj_client_sound) => patch({ dj_client_sound })} />
            <Field label="Φωτορυθμικά" value={draft.lights} disabled={locked} onChange={(lights) => patch({ lights })} />
            <Field label="Open bar" value={draft.open_bar} disabled={locked} onChange={(open_bar) => patch({ open_bar })} />
          </div>

          <h3>Δόσεις</h3>
          {draft.installments.map((row, index) => (
            <div className="fields" key={row.label}>
              <Field label={row.label} value={row.amount} disabled={locked} onChange={(amount) => setInstallment(index, { amount })} />
              <Field label="Προθεσμία" value={row.due} disabled={locked} onChange={(due) => setInstallment(index, { due })} />
            </div>
          ))}

          <h3>Λογαριασμοί</h3>
          {draft.banks.map((bank, index) => (
            <div className="fields" key={index}>
              <Field label="Τράπεζα" value={bank.bank} disabled={locked} onChange={(name) => setBank(index, { bank: name })} />
              <Field label="IBAN" value={bank.iban} disabled={locked} onChange={(iban) => setBank(index, { iban })} />
              <Field label="Δικαιούχος" value={bank.holder} disabled={locked} onChange={(holder) => setBank(index, { holder })} />
            </div>
          ))}

          <h3>Κείμενο που υπογράφει ο πελάτης</h3>
          <div className="contract-preview">
            <p>{contractIntro(draft)}</p>
            {articles.map((article) => (
              <section key={article.heading}>
                <h4>{article.heading}</h4>
                {article.lines.map((line, index) => <p key={`${article.heading}-${index}`}>{line}</p>)}
              </section>
            ))}
          </div>

          <div className="actions">
            {canEdit && visitId ? <button type="button" className="secondary" onClick={() => void save()} disabled={saving || sending}>{saving ? "Αποθήκευση…" : "Αποθήκευση"}</button> : null}
            {canEdit && visitId && status?.state !== "signed" ? <button type="button" onClick={() => void send()} disabled={sending || saving}>{sending ? "Αποστολή…" : "Αποστολή"}</button> : null}
          </div>
        </>
      )}
    </article>
  );

  function setParty(index: number, partial: Partial<ContractDraft["parties"][number]>) {
    patch({ parties: draft.parties.map((party, partyIndex) => (partyIndex === index ? { ...party, ...partial } : party)) });
  }

  function setInstallment(index: number, partial: Partial<ContractDraft["installments"][number]>) {
    setDraft((current) => ({ ...current, installments: current.installments.map((row, rowIndex) => (rowIndex === index ? { ...row, ...partial } : row)) }));
    setNote(null);
  }

  function setBank(index: number, partial: Partial<ContractDraft["banks"][number]>) {
    patch({ banks: draft.banks.map((bank, bankIndex) => (bankIndex === index ? { ...bank, ...partial } : bank)) });
  }
}

function Field({ label, value, onChange, disabled, type = "text" }: { label: string; value: string; onChange: (value: string) => void; disabled: boolean; type?: string }) {
  return (
    <label>{label}
      <input type={type} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
